// - 完整封装 axios，参考 ct_oms_pc/src/axios/index.ts
// 参考 ct_oms_pc 项目的 axios 封装方式，适配 Vue3 + JavaScript
// 使用VUE_APP_API_BASE_URL作为API服务基础URL

import axios from "axios";
import { ErrorCode, HttpError } from "@/utils/error-codes.js";
import {
  TokenManager,
  refreshToken,
  timeoutRetry,
} from "@/utils/axios-util.js";
import { Toast } from "vant";
import router from "@/router";

// 超时时间配置
const TIMEOUT = 5000;

/**
 * 创建 axios 实例
 */
const indexAxios = axios.create({
  timeout: TIMEOUT,
  baseURL: process.env.VUE_APP_API_BASE_URL,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json;charset=utf-8",
  },
});

/**
 * 请求拦截器
 * 功能：自动添加 Authorization token
 */
indexAxios.interceptors.request.use(
  (config) => {
    const token = TokenManager.getToken();

    // 避免刷新 token 时覆盖 refreshToken
    if (!config.headers.Authorization && token) {
      config.headers.Authorization = `Bearer ${token.accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器
 * 功能：统一处理响应和错误
 */
indexAxios.interceptors.response.use(
  (response) => {
    // 直接返回响应数据
    return Promise.resolve(response.data);
  },
  async (error) => {
    let errCode;
    let errMessage;

    // 网络错误
    if (error.code === "ERR_NETWORK") {
      errCode = ErrorCode.Http_ErrNetWork;
      errMessage = "网络连接出现错误：" + error.config?.url;
    }
    // 超时错误
    else if (
      error.code === "ECONNABORTED" &&
      error.message?.includes("timeout")
    ) {
      return timeoutRetry(indexAxios, error);
    }
    // HTTP 状态码错误
    else {
      const responseMessage =
        error.response?.data?.msg || error.response?.data?.message;

      // 请求被取消
      if (axios.isCancel(error)) {
        errCode = ErrorCode.Http_ErrCanceled;
        errMessage = "请求被取消";
      }
      // 401 未授权
      else if (error.response?.status === 401) {
        // 判断是否是刷新 token 的请求失败
        if (
          error.config?.data instanceof FormData &&
          error.config?.data?.get("grant_type") === "refresh_token"
        ) {
          errCode = ErrorCode.Http_RefreshError;
          errMessage = "登录已过期，请重新登录";
          Toast.fail(errMessage);
          setTimeout(() => {
            router.push({ name: "login" });
          }, 1500);
        }
        // 判断是否是登录请求失败
        else if (
          error.config?.data instanceof FormData &&
          ["sms_code", "password"].includes(
            error.config?.data?.get("grant_type")
          )
        ) {
          errCode = ErrorCode.Auth_Forbidden;
          errMessage = responseMessage || "未经授权";
        }
        // 其他 401 错误，尝试刷新 token
        else {
          return refreshToken(indexAxios, error);
        }
      }
      // 403 禁止访问
      else if (error.response?.status === 403) {
        errCode = ErrorCode.Auth_Forbidden;
        errMessage = `请求被拒绝：${responseMessage || error.message}`;
      }
      // 404 未找到
      else if (error.response?.status === 404) {
        errCode = ErrorCode.Http_ErrNotFound;
        errMessage = `请求地址不存在：${error.config?.url}`;
      }
      // 429 请求过于频繁
      else if (error.response?.status === 429) {
        errCode = ErrorCode.Http_ErrRequestTooMany;
        errMessage = responseMessage || `请求太过频繁：${error.message}`;
      }
      // 500 服务器错误
      else if (error.response?.status === 500) {
        errCode = ErrorCode.Http_ErrServer;
        errMessage = `服务端出现错误：${responseMessage || error.message}`;
      }
      // 其他错误
      else {
        errCode = ErrorCode.Http_ErrBadRequest;
        errMessage = `请求出现异常：${responseMessage || error.message}`;
      }
    }

    // 创建并返回自定义错误
    const httpError = new HttpError(errCode, errMessage, error);

    // 显示错误提示（可选，根据需要开启）
    // Toast.fail(errMessage);

    return Promise.reject(httpError);
  }
);

export { indexAxios, TokenManager };
